import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './Admin.css';

function AdminProfile({ onLogout }) {
  const [profile, setProfile] = useState({
    name: '',
    avatar: '',
    bio: '',
    social_links: []
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/profile`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setProfile(data);
      }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile({ ...profile, [name]: value });
  };

  const handleSocialLinkChange = (index, field, value) => {
    const newLinks = [...profile.social_links];
    newLinks[index][field] = value;
    setProfile({ ...profile, social_links: newLinks });
  };

  const addSocialLink = () => {
    setProfile({
      ...profile,
      social_links: [...profile.social_links, { platform: '', url: '', icon: 'link' }]
    });
  };

  const removeSocialLink = (index) => {
    const newLinks = profile.social_links.filter((_, i) => i !== index);
    setProfile({ ...profile, social_links: newLinks });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(profile)
      });

      if (response.ok) {
        setSaved(true);
        setTimeout(() => setSaved(false), 3000);
      } else {
        alert('Error saving profile');
      }
    } catch (error) {
      alert('Error saving profile');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="loading-container"><div className="loading-spinner"></div></div>;
  }

  return (
    <div className="admin-page" data-testid="admin-profile">
      <nav className="admin-nav">
        <div className="admin-nav-content">
          <h1 className="brand-text">ClutchBabe9 Admin</h1>
          <div className="admin-nav-links">
            <Link to="/admin/dashboard" className="admin-nav-link">Dashboard</Link>
            <Link to="/admin/posts" className="admin-nav-link">Posts</Link>
            <Link to="/admin/videos" className="admin-nav-link">Videos</Link>
            <Link to="/admin/subscribers" className="admin-nav-link">Subscribers</Link>
            <Link to="/admin/contacts" className="admin-nav-link">Contacts</Link>
            <Link to="/admin/profile" className="admin-nav-link active">Profile</Link>
            <Link to="/admin/config" className="admin-nav-link">Settings</Link>
            <Link to="/" className="admin-nav-link view-site-link" data-testid="view-site-btn">🌐 View Site</Link>
            <button onClick={() => { onLogout(); navigate('/admin/login'); }} className="btn btn-ghost">Logout</button>
          </div>
        </div>
      </nav>

      <div className="admin-content">
        <h2>Edit Profile</h2>
        <p style={{ color: 'var(--text-muted)', marginBottom: 'var(--spacing-xl)' }}>
          Update your profile information, bio, avatar, and social media links.
        </p>

        <form onSubmit={handleSubmit} className="admin-form">
          <div className="form-group">
            <label>Display Name</label>
            <input
              type="text"
              name="name"
              value={profile.name}
              onChange={handleChange}
              placeholder="ClutchBabe9"
              data-testid="profile-name-input"
            />
          </div>

          <div className="form-group">
            <label>Avatar/Profile Picture URL</label>
            <input
              type="url"
              name="avatar"
              value={profile.avatar}
              onChange={handleChange}
              placeholder="https://example.com/avatar.png"
              data-testid="profile-avatar-input"
            />
            <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.5rem', display: 'block' }}>
              Enter the URL of your profile picture/avatar image
            </small>
            {profile.avatar && (
              <div style={{ marginTop: 'var(--spacing-md)' }}>
                <img 
                  src={profile.avatar} 
                  alt="Avatar preview" 
                  style={{ 
                    width: '120px', 
                    height: '120px', 
                    objectFit: 'contain',
                    border: '2px solid var(--electric-blue)',
                    borderRadius: '50%',
                    padding: '8px',
                    background: 'rgba(0, 217, 255, 0.05)'
                  }} 
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>Bio / About Me</label>
            <textarea
              name="bio"
              value={profile.bio}
              onChange={handleChange}
              rows="12"
              placeholder="Tell your story..."
              data-testid="profile-bio-input"
            />
            <small style={{ color: 'var(--text-muted)', fontSize: '0.75rem', marginTop: '0.5rem', display: 'block' }}>
              This appears on your About page
            </small>
          </div>

          <div className="form-section-title">
            <h3 style={{ color: 'var(--text-primary)', marginTop: 'var(--spacing-xl)', marginBottom: 'var(--spacing-lg)' }}>
              Social Media Links
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem', marginBottom: 'var(--spacing-lg)' }}>
              Add, edit, or remove your social media profiles. These will appear on your About page and throughout the site.
            </p>
          </div>

          {profile.social_links.map((link, index) => (
            <div key={index} className="social-link-group" style={{ 
              display: 'grid', 
              gridTemplateColumns: '1fr 2fr 1fr auto', 
              gap: 'var(--spacing-md)', 
              marginBottom: 'var(--spacing-md)',
              alignItems: 'end'
            }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>Platform</label>
                <input
                  type="text"
                  value={link.platform}
                  onChange={(e) => handleSocialLinkChange(index, 'platform', e.target.value)}
                  placeholder="YouTube"
                  data-testid={`social-platform-${index}`}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>URL</label>
                <input
                  type="url"
                  value={link.url}
                  onChange={(e) => handleSocialLinkChange(index, 'url', e.target.value)}
                  placeholder="https://youtube.com/@yourhandle"
                  data-testid={`social-url-${index}`}
                />
              </div>

              <div className="form-group" style={{ margin: 0 }}>
                <label>Icon</label>
                <input
                  type="text"
                  value={link.icon}
                  onChange={(e) => handleSocialLinkChange(index, 'icon', e.target.value)}
                  placeholder="youtube"
                  data-testid={`social-icon-${index}`}
                />
              </div>

              <button
                type="button"
                onClick={() => removeSocialLink(index)}
                className="btn-sm btn-danger"
                style={{ marginBottom: '2px' }}
                data-testid={`remove-social-${index}`}
              >
                Remove
              </button>
            </div>
          ))}

          <button
            type="button"
            onClick={addSocialLink}
            className="btn btn-secondary"
            style={{ marginTop: 'var(--spacing-md)', marginBottom: 'var(--spacing-xl)' }}
            data-testid="add-social-link-btn"
          >
            + Add Social Link
          </button>

          <div className="form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving} data-testid="save-profile-btn">
              {saving ? 'Saving...' : saved ? '✓ Saved!' : 'Save Profile'}
            </button>
          </div>

          {saved && (
            <div style={{
              marginTop: 'var(--spacing-lg)',
              padding: 'var(--spacing-md)',
              background: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.3)',
              borderRadius: 'var(--radius-sm)',
              color: 'var(--success)',
              textAlign: 'center'
            }}>
              ✓ Profile saved successfully! Changes will appear on your public site.
            </div>
          )}
        </form>
      </div>
    </div>
  );
}

export default AdminProfile;
